import React, { useEffect, useState } from 'react';
import { Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { syncManager } from '../services/sync';
import { db, UserEntry } from '../db';

const SyncBadge = () => {
    const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
        const [isSyncing, setIsSyncing] = useState<boolean>(false);

        // Real-time count of pending items
        const [pendingCount, setPendingCount] = useState<number>(0);
        const [userMetadata, setUserMetadata] = useState<UserEntry | null>(null);

    useEffect(() => {
        const checkCount = () => {
                    db.logs.where('status').equals('pending').count().then(setPendingCount);
                    db.user.toCollection().first().then((user) => {
                        setUserMetadata(user || null);
                    });
                };
        checkCount();
        const interval = setInterval(checkCount, 3000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        // Listen for network changes
        const handleOnline = () => { setIsOnline(true); autoSync(); };
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Initial sync check
        if (navigator.onLine) {autoSync();}

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const autoSync = async () => {
        if (pendingCount > 0) {handleSync();}
    };

    const handleSync = async () => {
        if (!navigator.onLine) {return;}
        setIsSyncing(true);
        await syncManager.syncNow();
        setIsSyncing(false);
    };

    // RENDER STATES

    // 1. Offline Mode
    if (!isOnline) {
        return (
            <div className="flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-bold shadow-sm">
                <WifiOff size={14} />
                <span>Offline ({pendingCount || 0})</span>
            </div>
        );
    }

    // 2. Online + Pending Items
    if (pendingCount > 0) {
        return (
            <div
                onClick={handleSync}
                className="cursor-pointer flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold shadow-sm hover:bg-blue-200 transition-colors"
            >
                <RefreshCw size={14} className={isSyncing ? "animate-spin" : ""} />
                <span>{isSyncing ? "Syncing..." : `Sync (${pendingCount})`}</span>
            </div>
        );
    }

    // 3. All Good
    return (
        <div className="flex items-center gap-2">
            {userMetadata?.networkStatus && (
                <div className={`px-4 py-1.5 rounded-2xl text-[10px] font-black uppercase tracking-widest border transition-all ${userMetadata.networkStatus === 'Network Member' ? 'bg-blue-50 text-blue-600 border-blue-100 shadow-sm' :
                    userMetadata.networkStatus === 'Network Coach' ? 'bg-indigo-50 text-indigo-600 border-indigo-100 shadow-sm' :
                        'bg-slate-50 text-slate-400 border-slate-100'
                    }`}>
                    {userMetadata.networkStatus}
                </div>
            )}
            <div
                onClick={handleSync}
                className="flex items-center gap-2 bg-slate-50 text-slate-400 px-4 py-1.5 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-slate-100 cursor-pointer hover:bg-slate-100 transition-colors"
                title={isSyncing ? "Syncing data with headquarters..." : "All local data is synced. Click to force a remote refresh."}
            >
                <Wifi size={12} className={isOnline ? "text-blue-500" : "text-slate-300"} />
                <span>{isSyncing ? "Syncing..." : "Synced"}</span>
            </div>
        </div>
    );
};

export default SyncBadge;
